import { FC } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { RootState } from './store'
import ProtectedRoute from './components/ProtectedRoute'
import RoleProtectedRoute from './components/RoleProtectedRoute'
import AuthProvider from './components/AuthProvider'
import ActiveTestRoute from './components/ActiveTestRoute'
import Layout from './components/Layout'

// Pages
import AuthPage from './pages/auth/AuthPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import DashboardPage from './pages/DashboardPage'
import TestPage from './pages/test/TestPage'
import TestInstructionsPage from './pages/test/TestInstructionsPage'
import TestSystemCheckPage from './pages/test/TestSystemCheckPage'
import TestCompletedPage from './pages/test/TestCompletedPage'
import TestHistoryPage from './pages/test/TestHistoryPage'
import CreateDrivePage from './pages/recruiter/CreateDrivePage'
import CollegeRegistrationPage from './pages/recruiter/CollegeRegistrationPage'
import MonitoringRecordsPage from './pages/recruiter/MonitoringRecordsPage'
import DrivesListPage from './pages/recruiter/DrivesListPage'
import DriveDetailPage from './pages/recruiter/DriveDetailPage'
import EditDrivePage from './pages/recruiter/EditDrivePage'
import CreateAssessmentPage from './pages/recruiter/CreateAssessmentPage'
import QuestionBankCategoryPage from './pages/recruiter/QuestionBankCategoryPage'
import QuestionBankTypePage from './pages/recruiter/QuestionBankTypePage'
import QuestionBankAddPage from './pages/recruiter/QuestionBankAddPage'
import CreateCodingQuestionPage from './pages/recruiter/CreateCodingQuestionPage'
import QuestionsPage from './pages/recruiter/QuestionsPage'
import DriveRegistrationPage from './pages/candidate/DriveRegistrationPage'
import AdminReportPage from './pages/admin/AdminReportPage'
import ProfilePage from './pages/ProfilePage'
import NotFoundPage from './pages/NotFoundPage'

const App: FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth)

  return (
    <AuthProvider>
      <div className="min-h-screen bg-gray-50">
        <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={!isAuthenticated ? <AuthPage /> : <Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/register" 
          element={!isAuthenticated ? <AuthPage /> : <Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/register/:linkId" 
          element={<DriveRegistrationPage />} 
        />
        <Route 
          path="/forgot-password" 
          element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/reset-password" 
          element={!isAuthenticated ? <ResetPasswordPage /> : <Navigate to="/dashboard" replace />} 
        />
        
        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route 
            path="admin/report" 
            element={
              <RoleProtectedRoute allowedRoles={['admin']}>
                <AdminReportPage />
              </RoleProtectedRoute>
            } 
          />
          <Route path="profile" element={<ProfilePage />} />
          <Route 
            path="test-history" 
            element={
              <RoleProtectedRoute allowedRoles={['candidate']}>
                <TestHistoryPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="test/:sessionId/instructions" 
            element={
              <RoleProtectedRoute allowedRoles={['candidate']}>
                <ActiveTestRoute><TestInstructionsPage /></ActiveTestRoute>
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="test/:sessionId/system-check" 
            element={
              <RoleProtectedRoute allowedRoles={['candidate']}>
                <ActiveTestRoute><TestSystemCheckPage /></ActiveTestRoute>
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="test/:sessionId" 
            element={
              <RoleProtectedRoute allowedRoles={['candidate']}>
                <ActiveTestRoute><TestPage /></ActiveTestRoute>
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="test/:sessionId/completed" 
            element={
              <RoleProtectedRoute allowedRoles={['candidate']}>
                <TestCompletedPage />
              </RoleProtectedRoute>
            } 
          />
          
          {/* Recruiter routes */}
          <Route path="recruiter/monitoring" element={<RoleProtectedRoute allowedRoles={['recruiter']}><MonitoringRecordsPage /></RoleProtectedRoute>} />
          <Route path="recruiter/colleges" element={<RoleProtectedRoute allowedRoles={['recruiter']}><CollegeRegistrationPage /></RoleProtectedRoute>} />
          <Route 
            path="recruiter/drives/new" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <CreateDrivePage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/drives" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <DrivesListPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/drives/:id" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <DriveDetailPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/drives/:id/edit" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <EditDrivePage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/assessments/new" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <CreateAssessmentPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/assessments" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <CreateAssessmentPage />
              </RoleProtectedRoute>
            } 
          />
          
          {/* Question Bank Routes */}
          <Route 
            path="recruiter/question-bank" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <QuestionBankCategoryPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/question-bank/type" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <QuestionBankTypePage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/question-bank/add" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <QuestionBankAddPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/coding-questions/create" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <CreateCodingQuestionPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/questions/:category/:assessmentType" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <QuestionsPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/candidates" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <DashboardPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/analytics" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <DashboardPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/reports" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <DashboardPage />
              </RoleProtectedRoute>
            } 
          />
          <Route 
            path="recruiter/dashboard" 
            element={
              <RoleProtectedRoute allowedRoles={['recruiter']}>
                <DashboardPage />
              </RoleProtectedRoute>
            } 
          />
        </Route>
        
        {/* 404 page */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </AuthProvider>
  )
}

export default App
